import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Wifi, WifiOff, Send, Link2, Check } from "lucide-react";
import AvatarPicker from "@/components/AvatarPicker";
import TopBar from "@/components/TopBar";
import { wsBase, API } from "@/lib/api";
import { resolveAvatarUrl } from "@/lib/avatars";
import axios from "axios";

/**
 * Shared chat-room screen used by:
 *   - Big Table (public): mode="big"
 *   - Small Table (private): mode="small", with `roomKey` provided
 *
 * Avatars use the painterly Nano-Banana pool via resolveAvatarUrl().
 * Frames are rectangular (w-12 h-14) to match the requested grim portrait look.
 */
export default function ChatRoom({ mode = "big" }) {
  const navigate = useNavigate();
  const { key: paramKey } = useParams();
  const roomKey = (paramKey || "").toUpperCase();

  const [identity, setIdentity] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [alive, setAlive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [roomName, setRoomName] = useState("");
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const queueRef = useRef([]);

  const wsUrl =
    mode === "small"
      ? `${wsBase()}/ws/tavern-room/${encodeURIComponent(roomKey)}`
      : `${wsBase()}/ws/tavern`;
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}${mode === "small" ? `/tavern/room/${roomKey}` : "/tavern/big"}`
    : "";

  // Look up the small table's display name on mount.
  useEffect(() => {
    if (mode !== "small" || !roomKey) return;
    let cancelled = false;
    axios.get(`${API}/tavern-rooms`).then((res) => {
      if (cancelled) return;
      const found = (res.data || []).find((r) => r.key === roomKey);
      if (found) setRoomName(found.name);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [mode, roomKey]);

  const flush = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    while (queueRef.current.length) {
      const m = queueRef.current.shift();
      try { ws.send(JSON.stringify(m)); } catch { queueRef.current.unshift(m); break; }
    }
  }, []);

  useEffect(() => {
    if (!identity) return;
    let cancelled = false;
    let pingTimer = null;
    let reconnectTimer = null;
    let attempts = 0;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        attempts = 0;
        setAlive(true);
        try { ws.send(JSON.stringify({ type: "join", name: identity.name, avatar: identity.seed })); } catch {}
        flush();
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: "ping", t: Date.now() })); } catch {}
          }
        }, 10000);
      };
      ws.onmessage = (ev) => {
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === "pong" || m.type === "ping") return;
        if (m.type === "error" && m.error === "room_not_found") {
          setError("This small table no longer exists. Return to the tavern hall.");
          setAlive(false);
          ws.close();
          return;
        }
        if (m.type === "history") {
          // Drop any system messages from history.
          const arr = Array.isArray(m.messages) ? m.messages.filter((x) => x && x.type === "chat") : [];
          setMessages(arr);
          if (m.name) setRoomName(m.name);
        } else if (m.type === "chat") {
          setMessages((prev) => [...prev.slice(-199), m]);
        }
      };
      ws.onclose = () => {
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (cancelled) return;
        setAlive(false);
        attempts += 1;
        const delay = Math.min(5000, 500 * Math.pow(2, attempts));
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
      wsRef.current = null;
    };
  }, [identity, wsUrl, flush]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (bottomRef.current && typeof bottomRef.current.scrollIntoView === "function") {
        bottomRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [messages.length]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    const payload = { type: "chat", text };
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(payload)); } catch { queueRef.current.push(payload); }
    } else {
      queueRef.current.push(payload);
    }
    setDraft("");
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); }
      finally { document.body.removeChild(ta); }
    }
  };

  if (!identity) {
    return (
      <AvatarPicker
        roleColor="red"
        roleLabel="Tavern Patron"
        proceedLabel={mode === "small" ? "To the Small Table" : "To the Big Table"}
        pool="tavern"
        onComplete={({ seed, name }) => setIdentity({ seed, name })}
        onCancel={() => navigate("/tavern")}
      />
    );
  }

  const headerLabel = mode === "small"
    ? (roomName ? `${roomName} · ${roomKey}` : `Small Table · ${roomKey}`)
    : "Big Table";

  return (
    <div className="relative min-h-screen overflow-hidden flex flex-col" data-testid="chatroom-page">
      <div className="absolute inset-0 tapestry-grid pointer-events-none opacity-50" />
      <TopBar
        theme="tavern"
        onBack={() => navigate("/tavern")}
        backLabel="Tavern"
        testId="leave-chatroom-button"
        center={
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight truncate"
              style={{ color: "#E5C870", textShadow: "0 1px 0 rgba(0,0,0,0.7), 0 0 14px rgba(201,162,74,0.4)" }}>
            {headerLabel}
          </h2>
        }
        right={
          <div className="text-[10px] tracking-[0.3em] uppercase font-display flex items-center gap-1">
            {alive ? (
              <span className="text-emerald-300 flex items-center gap-1" data-testid="chatroom-status-on">
                <Wifi size={12} /> <span className="hidden sm:inline">{identity.name}</span>
              </span>
            ) : (
              <span className="text-rose-400 flex items-center gap-1" data-testid="chatroom-status-off">
                <WifiOff size={12} /> <span className="hidden sm:inline">{error || "Reconnecting…"}</span>
              </span>
            )}
          </div>
        }
      />
      <div className="relative z-10 max-w-3xl w-full mx-auto px-4 pt-20 pb-8 flex-1 flex flex-col">

        <div className="mb-3 arcade-panel px-4 py-3 flex items-center gap-3" data-testid="chatroom-share-bar">
          <Link2 size={16} className="text-amber-700 shrink-0" />
          <div className="text-[10px] tracking-[0.2em] uppercase text-stone-700 font-display shrink-0">
            Share Link
          </div>
          <input
            readOnly
            value={shareUrl}
            onClick={(e) => e.target.select()}
            className="arcade-input flex-1 text-xs font-mono"
            data-testid="chatroom-share-url"
          />
          <button
            onClick={copyShare}
            className="arcade-btn arcade-btn-magenta !px-3 !py-1.5 text-xs shrink-0"
            data-testid="chatroom-copy-button"
            aria-label="Copy share link"
          >
            {copied ? <><Check size={14} /> Copied</> : <>Copy</>}
          </button>
        </div>

        <div className="arcade-panel p-3 md:p-4 flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="chatroom-messages">
            {messages.length === 0 && (
              <div className="text-stone-700 italic text-sm text-center py-8" data-testid="chatroom-empty">
                The hearth crackles. No one has spoken yet — be the first.
              </div>
            )}
            {messages.map((m, i) => {
              const mine = m.name === identity.name;
              const stableKey = m.ts ? `${m.ts}-${m.name}` : `idx-${i}`;
              return (
                <div key={stableKey} className={`flex items-start gap-2 ${mine ? "justify-end" : "justify-start"}`}
                     data-testid={`chatroom-message-${i}`}>
                  {!mine && m.avatar && resolveAvatarUrl(m.avatar) && (
                    <img src={resolveAvatarUrl(m.avatar)} alt={m.name}
                         onError={(e) => { e.currentTarget.style.display = "none"; }}
                         style={{ filter: "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }}
                         className="w-12 h-14 object-cover rounded-sm border-2 border-red-800/60 bg-stone-800 shrink-0" />
                  )}
                  <div className={`max-w-[75%] px-3 py-1.5 border ${mine ? "border-blue-700/40 bg-blue-100/40" : "border-red-700/40 bg-red-100/30"}`}>
                    <div className={`text-[10px] tracking-widest uppercase font-display ${mine ? "text-blue-800" : "text-red-800"}`}>
                      {m.name}
                    </div>
                    <div className="text-stone-900 break-words whitespace-pre-wrap text-sm">{m.text}</div>
                  </div>
                  {mine && m.avatar && resolveAvatarUrl(m.avatar) && (
                    <img src={resolveAvatarUrl(m.avatar)} alt={m.name}
                         onError={(e) => { e.currentTarget.style.display = "none"; }}
                         style={{ filter: "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }}
                         className="w-12 h-14 object-cover rounded-sm border-2 border-blue-800/60 bg-stone-800 shrink-0" />
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} aria-hidden="true" />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="mt-3 flex gap-2 items-stretch"
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Speak thy mind…"
              className="arcade-input flex-1"
              data-testid="chatroom-input"
            />
            <button
              type="submit"
              className="arcade-btn arcade-btn-magenta !px-5 !py-2 text-xs"
              disabled={!draft.trim()}
              data-testid="chatroom-send"
            >
              <Send size={14} /> Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
